import React, { useEffect, useRef } from 'react';
import { Play, Target, TrendingUp, Zap, Award, ArrowRight, CheckCircle, BarChart3, Sparkles, Star, Search, DollarSign, Users } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Link } from 'react-router-dom';  

gsap.registerPlugin(ScrollTrigger);

const BingAdsPage = () => {
  const containerRef = useRef(null);
  const heroRef = useRef(null);
  const floatingElementsRef = useRef([]);
  const statsRef = useRef([]);
  const sectionsRef = useRef([]);
  const cardsRef = useRef([]);
  const orbsRef = useRef([]);

  useEffect(() => {
    const container = containerRef.current;
    const hero = heroRef.current;
    const floatingElements = floatingElementsRef.current;
    const stats = statsRef.current;
    const sections = sectionsRef.current;
    const cards = cardsRef.current;
    const orbs = orbsRef.current;

    // Floating orbs animation
    orbs.forEach((orb, index) => {
      gsap.to(orb, {
        y: index % 2 === 0 ? -50 : 50,
        x: index % 2 === 0 ? 30 : -30,
        rotation: 360,
        duration: 8 + index * 2,
        repeat: -1,
        yoyo: true,
        ease: "power2.inOut",
      });
    });

    // Hero section animation
    gsap.fromTo(hero.querySelector('.hero-content'),
      { opacity: 0, y: 100, scale: 0.9 },
      {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 1.5,
        ease: "power3.out",
        delay: 0.5
      }
    );

    // Floating elements animation
    floatingElements.forEach((element, index) => {
      gsap.to(element, {
        y: -20,
        rotation: 360,
        duration: 4 + index,
        repeat: -1,
        yoyo: true,
        ease: "power2.inOut",
        delay: index * 0.5
      });
    });

    // Stats counter animation
    const animateStats = () => {
      const targets = [1.2, 35, 280, 87];
      stats.forEach((stat, index) => {
        const counter = { value: 0 };
        gsap.to(counter, {
          value: targets[index],
          duration: 2,
          ease: "power2.out",
          onUpdate: () => {
            stat.textContent = targets[index] < 10 ? counter.value.toFixed(1) : Math.floor(counter.value);
          }
        });
      });
    };

    // Stats section scroll trigger
    ScrollTrigger.create({
      trigger: ".stats-section",
      start: "top 80%",
      onEnter: animateStats,
      once: true
    });

    // Sections stagger animation
    sections.forEach((section, index) => {
      gsap.fromTo(section,
        { opacity: 0, y: 80, rotationX: 45 },
        {
          opacity: 1,
          y: 0,
          rotationX: 0,
          duration: 1.2,
          ease: "power3.out",
          scrollTrigger: {
            trigger: section,
            start: "top 85%",
            toggleActions: "play none none reverse"
          }
        }
      );
    });

    // Cards animation
    cards.forEach((card, index) => {
      gsap.fromTo(card,
        { opacity: 0, y: 60, scale: 0.8 },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.8,
          delay: index * 0.1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: card,
            start: "top 90%",
            toggleActions: "play none none reverse"
          }
        }
      );
    });

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  const addFloatingRef = (el) => {
    if (el && !floatingElementsRef.current.includes(el)) {
      floatingElementsRef.current.push(el);
    }
  };

  const addStatsRef = (el) => {
    if (el && !statsRef.current.includes(el)) {
      statsRef.current.push(el);
    }
  };

  const addSectionRef = (el) => {
    if (el && !sectionsRef.current.includes(el)) {
      sectionsRef.current.push(el);
    }
  };

  const addCardRef = (el) => {
    if (el && !cardsRef.current.includes(el)) {
      cardsRef.current.push(el);
    }
  };

  const addOrbRef = (el) => {
    if (el && !orbsRef.current.includes(el)) {
      orbsRef.current.push(el);
    }
  };

  return (
    <>
      <div
        ref={containerRef}
        style={{
          position: "relative",
          background: "linear-gradient(180deg, #000 0%, #0a0a0a 50%, #000 100%)",
          color: "#fff",
          overflow: "hidden",
          paddingTop: "0px",
        }}
      >
        {/* Background Orbs */}
        <div
          ref={addOrbRef}
          style={{
            position: "absolute",
            width: "600px",
            height: "600px",
            background: "radial-gradient(circle, rgba(0, 120, 212, 0.08) 0%, transparent 70%)",
            borderRadius: "50%",
            top: "10%",
            right: "-15%",
            filter: "blur(120px)",
            zIndex: 1,
          }}
        />
        <div
          ref={addOrbRef}
          style={{
            position: "absolute",
            width: "500px",
            height: "500px",
            background: "radial-gradient(circle, rgba(255, 185, 0, 0.06) 0%, transparent 70%)",
            borderRadius: "50%",
            bottom: "20%",
            left: "-10%",
            filter: "blur(100px)",
            zIndex: 1,
          }}
        />
        <div
          ref={addOrbRef}
          style={{
            position: "absolute",
            width: "400px",
            height: "400px",
            background: "radial-gradient(circle, rgba(83, 176, 57, 0.05) 0%, transparent 70%)",
            borderRadius: "50%",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            filter: "blur(80px)",
            zIndex: 1,
          }}
        />

        {/* Floating Particles */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundImage: `
              radial-gradient(2px 2px at 20px 30px, rgba(255, 255, 255, 0.1), transparent),
              radial-gradient(2px 2px at 40px 70px, rgba(0, 120, 212, 0.1), transparent),
              radial-gradient(1px 1px at 90px 40px, rgba(255, 185, 0, 0.1), transparent),
              radial-gradient(1px 1px at 130px 80px, rgba(83, 176, 57, 0.1), transparent)
            `,
            backgroundRepeat: "repeat",
            backgroundSize: "150px 100px",
            animation: "particleFloat 20s linear infinite",
            zIndex: 1,
          }}
        />

        {/* Floating Background Elements */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 2,
          }}
        >
          <div
            ref={addFloatingRef}
            style={{
              position: "absolute",
              top: "20%",
              left: "10%",
              width: "60px",
              height: "60px",
              background: "rgba(0, 120, 212, 0.1)",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#0078D4",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(0, 120, 212, 0.2)",
            }}
          >
            <Search size={24} />
          </div>
          <div
            ref={addFloatingRef}
            style={{
              position: "absolute",
              top: "30%",
              right: "15%",
              width: "70px",
              height: "70px",
              background: "rgba(255, 185, 0, 0.1)",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#FFB900",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 185, 0, 0.2)",
            }}
          >
            <DollarSign size={32} />
          </div>
          <div
            ref={addFloatingRef}
            style={{
              position: "absolute",
              top: "60%",
              left: "5%",
              width: "65px",
              height: "65px",
              background: "rgba(83, 176, 57, 0.1)",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#53B039",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(83, 176, 57, 0.2)",
            }}
          >
            <Users size={28} />
          </div>
          <div
            ref={addFloatingRef}
            style={{
              position: "absolute",
              top: "70%",
              right: "20%",
              width: "55px",
              height: "55px",
              background: "rgba(0, 120, 212, 0.1)",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#0078D4",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(0, 120, 212, 0.2)",
            }}
          >
            <Award size={26} />
          </div>
          <div
            ref={addFloatingRef}
            style={{
              position: "absolute",
              top: "40%",
              left: "80%",
              width: "68px",
              height: "68px",
              background: "rgba(255, 185, 0, 0.1)",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#FFB900",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 185, 0, 0.2)",
            }}
          >
            <BarChart3 size={30} />
          </div>
        </div>

        {/* Hero Section */}
        <section
          ref={heroRef}
          style={{
            position: "relative",
            zIndex: 10,
            padding: "8rem 2rem 6rem",
            textAlign: "center",
          }}
        >
          <div
            className="hero-content"
            style={{
              maxWidth: "1200px",
              margin: "0 auto",
            }}
          >
            {/* Hero Badge */}
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                padding: "0.75rem 1.5rem",
                background: "rgba(0, 0, 0, 0.6)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(0, 120, 212, 0.4)",
                borderRadius: "50px",
                fontSize: "0.95rem",
                fontWeight: "600",
                marginBottom: "3rem",
                color: "#fff",
                boxShadow: "0 4px 15px rgba(0, 120, 212, 0.15)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: "-50%",
                  width: "50%",
                  height: "100%",
                  background: "linear-gradient(120deg, transparent, rgba(255, 255, 255, 0.5), transparent)",
                  transform: "skewX(-20deg)",
                  animation: "shimmer 3s infinite",
                }}
              />
              <Sparkles size={18} style={{ color: "#0078D4" }} />
              Bing Ads Excellence
            </div>

            <h1
              style={{
                fontSize: "clamp(3rem, 6vw, 5rem)",
                fontWeight: "900",
                lineHeight: "1.1",
                marginBottom: "2rem",
                color: "#fff",
              }}
            >
              Bing Ads: The Growth Channel Nobody {" "}
              <span
                style={{
                  background: "linear-gradient(135deg, #0078D4, #FFB900, #53B039)",
                  backgroundSize: "200% 100%",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  color: "transparent",
                  animation: "gradientShift 3s ease-in-out infinite",
                }}
              >
             Talks About
              </span>
            </h1>

            <p
              style={{
                fontSize: "1.3rem",
                fontWeight: "600",
                color: "#0078D4",
                marginBottom: "1.5rem",
              }}
            >
              Bing Ads Excellence by Beyond the Analysis
            </p>

            <p
              style={{
                fontSize: "clamp(1rem, 1.2vw, 1.2rem)",
                color: "rgba(255, 255, 255, 0.8)",
                lineHeight: "1.7",
                maxWidth: "900px",
                margin: "0 auto 3rem",
              }}
            >
              While everyone fights for Google traffic, smart businesses are winning on Bing. With over 1.2 billion 
              monthly searches and 35% lower competition, Bing Ads deliver higher-quality traffic at lower costs. 
              Our Bing specialists help you tap into this underutilized goldmine, reaching affluent, professional 
              audiences who convert at premium rates. Stop overpaying for clicks—start dominating where others overlook.
            </p>
                  <Link to="/consultation">

            <button
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "1rem",
                padding: "1.5rem 3rem",
                background: "linear-gradient(135deg, #0078D4, #FFB900)",
                color: "#fff",
                border: "none",
                borderRadius: "50px",
                fontSize: "1.2rem",
                fontWeight: "700",
                cursor: "pointer",
                transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                boxShadow: "0 8px 25px rgba(0, 120, 212, 0.3)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-5px) scale(1.05)";
                e.currentTarget.style.boxShadow = "0 20px 40px rgba(0, 120, 212, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0) scale(1)";
                e.currentTarget.style.boxShadow = "0 8px 25px rgba(0, 120, 212, 0.3)";
              }}
            >
              <Play size={24} />
              Discover Bing's Advantage
              <ArrowRight size={24} />
            </button>
            </Link> 
          </div>
        </section>

        {/* Stats Section */}
        <section
          className="stats-section"
          style={{
            position: "relative",
            zIndex: 10,
            padding: "4rem 2rem",
            background: "rgba(255, 255, 255, 0.02)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            borderRadius: "24px",
            margin: "0 2rem 6rem",
            maxWidth: "1200px",
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "1px",
              background: "linear-gradient(90deg, transparent, rgba(0, 120, 212, 0.5), transparent)",
            }}
          />
          
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "3rem",
              textAlign: "center",
            }}
          >
            {[
              { number: 1.2, label: "Billion Monthly Searches", color: "#0078D4" },
              { number: 35, label: "% Lower Competition", color: "#FFB900" },
              { number: 280, label: "% Average ROAS Increase", color: "#53B039" },
              { number: 87, label: "Campaigns Completed", color: "#0078D4" },
            ].map((stat, index) => (
              <div key={index} style={{ position: "relative" }}>
                <div
                  style={{
                    position: "absolute",
                    top: "-20px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "40px",
                    height: "40px",
                    background: `linear-gradient(135deg, ${stat.color}, ${stat.color}80)`,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Star size={20} style={{ color: "#000" }} />
                </div>
                <div
                  ref={addStatsRef}
                  style={{
                    fontSize: "3rem",
                    fontWeight: "900",
                    background: `linear-gradient(135deg, ${stat.color}, ${stat.color}80)`,
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    color: "transparent",
                    marginBottom: "0.5rem",
                    marginTop: "1rem",
                  }}
                >
                  0
                </div>
                <div
                  style={{
                    color: "rgba(255, 255, 255, 0.8)",
                    fontSize: "1rem",
                    fontWeight: "500",
                  }}
                >
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Why Bing Ads Matter */}
        <section
          ref={addSectionRef}
          style={{
            position: "relative",
            zIndex: 10,
            padding: "6rem 2rem",
            maxWidth: "1200px",
            margin: "0 auto",
            textAlign: "center",
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              padding: "0.75rem 1.5rem",
              background: "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 185, 0, 0.4)",
              borderRadius: "50px",
              fontSize: "0.95rem",
              fontWeight: "600",
              marginBottom: "3rem",
              color: "#fff",
              boxShadow: "0 4px 15px rgba(255, 185, 0, 0.15)",
            }}
          >
            <Target size={18} style={{ color: "#FFB900" }} />
            Strategic Importance
          </div>

          <h2
            style={{
              fontSize: "clamp(2.5rem, 5vw, 4rem)",
              fontWeight: "900",
              lineHeight: "1.2",
              marginBottom: "2rem",
              color: "#fff",
            }}
          >
            The Bing Ads{" "}
            <span
              style={{
                background: "linear-gradient(135deg, #FFB900, #53B039)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
              }}
            >
              Advantage
            </span>
          </h2>

          <div
            style={{
              background: "rgba(255, 255, 255, 0.02)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              borderRadius: "24px",
              padding: "3rem",
              marginBottom: "3rem",
            }}
          >
            <p
              style={{
                fontSize: "clamp(1rem, 1.2vw, 1.2rem)",
                color: "rgba(255, 255, 255, 0.8)",
                lineHeight: "1.7",
                marginBottom: "2rem",
                maxWidth: "800px",
                margin: "0 auto 2rem",
              }}
            >
              Bing powers over 1.2 billion monthly searches across Microsoft's ecosystem, including Windows, Xbox, 
              and Office. With 35% less competition than Google, your ads get better placement at lower costs, 
              reaching a unique audience of professionals and decision-makers.
            </p>
            <p
              style={{
                fontSize: "clamp(1rem, 1.2vw, 1.2rem)",
                color: "rgba(255, 255, 255, 0.8)",
                lineHeight: "1.7",
                maxWidth: "800px",
                margin: "0 auto",
              }}
            >
              Bing users typically have higher incomes, longer session durations, and better conversion rates. 
              While your competitors battle for expensive Google clicks, you can dominate Bing's premium audience 
              at a fraction of the cost.
            </p>
          </div>
                 <Link to="/consultation">
          <button
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.75rem",
              padding: "1.2rem 2.5rem",
              background: "linear-gradient(135deg, #FFB900, #53B039)",
              color: "#fff",
              border: "none",
              borderRadius: "50px",
              fontSize: "1.1rem",
              fontWeight: "700",
              cursor: "pointer",
              transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-3px)";
              e.currentTarget.style.boxShadow = "0 15px 40px rgba(255, 185, 0, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            Tap Into Premium Traffic
            <ArrowRight size={20} />
          </button>
          </Link>
        </section>

        {/* Services Section */}
        <section
          ref={addSectionRef}
          style={{
            position: "relative",
            zIndex: 10,
            padding: "6rem 2rem",
            maxWidth: "1200px",
            margin: "0 auto",
            textAlign: "center",
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              padding: "0.75rem 1.5rem",
              background: "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(83, 176, 57, 0.4)",
              borderRadius: "50px",
              fontSize: "0.95rem",
              fontWeight: "600",
              marginBottom: "3rem",
              color: "#fff",
              boxShadow: "0 4px 15px rgba(83, 176, 57, 0.15)",
            }}
          >
            <Award size={18} style={{ color: "#53B039" }} />
            Premium Services
          </div>

          <h2
            style={{
              fontSize: "clamp(2.5rem, 5vw, 4rem)",
              fontWeight: "900",
              lineHeight: "1.2",
              marginBottom: "1rem",
              color: "#fff",
            }}
          >
            Why Smart Businesses Choose{" "}
            <span
              style={{
                background: "linear-gradient(135deg, #53B039, #0078D4)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
              }}
            >
              Bing Ads
            </span>
          </h2>

          <p
            style={{
              fontSize: "1.2rem",
              fontWeight: "600",
              color: "#53B039",
              marginBottom: "2rem",
            }}
          >
            The Untapped Opportunity
          </p>

          <p
            style={{
              fontSize: "clamp(1rem, 1.2vw, 1.2rem)",
              color: "rgba(255, 255, 255, 0.8)",
              lineHeight: "1.7",
              maxWidth: "900px",
              margin: "0 auto 4rem",
            }}
          >
            While everyone crowds Google, Bing offers a blue ocean of opportunity. Lower competition means 
            better ad positions, higher click-through rates, and more cost-effective conversions. Our Bing 
            specialists help you capitalize on this advantage with strategic campaigns that deliver premium results.
          </p>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: "1.5rem",
              marginBottom: "4rem",
            }}
          >
            {[
              "35% lower cost-per-click compared to Google Ads",
              "Higher-income audience with 25% more purchasing power",
              "Better ad positions with less competition",
              "Seamless integration with Microsoft's business ecosystem",
            ].map((achievement, index) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                  padding: "1.5rem",
                  background: "rgba(255, 255, 255, 0.02)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  borderRadius: "16px",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)";
                  e.currentTarget.style.transform = "translateX(10px)";
                  e.currentTarget.style.borderColor = "rgba(83, 176, 57, 0.3)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.02)";
                  e.currentTarget.style.transform = "translateX(0)";
                  e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.1)";
                }}
              >
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    background: "linear-gradient(135deg, #53B039, #0078D4)",
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <CheckCircle size={20} style={{ color: "#000" }} />
                </div>
                <span
                  style={{
                    color: "rgba(255, 255, 255, 0.9)",
                    fontSize: "1rem",
                    fontWeight: "500",
                  }}
                >
                  {achievement}
                </span>
              </div>
            ))}
          </div>
                  <Link to="/consultation">
          <button
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.75rem",
              padding: "1.2rem 2.5rem",
              background: "linear-gradient(135deg, #53B039, #0078D4)",
              color: "#fff",
              border: "none",
              borderRadius: "50px",
              fontSize: "1.1rem",
              fontWeight: "700",
              cursor: "pointer",
              transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-3px)";
              e.currentTarget.style.boxShadow = "0 15px 40px rgba(83, 176, 57, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            Start Your Bing Strategy
            <ArrowRight size={20} />
          </button>
          </Link> 
        </section>

        {/* Campaign Types */}
        <section
          ref={addSectionRef}
          style={{
            position: "relative",
            zIndex: 10,
            padding: "6rem 2rem",
            maxWidth: "1200px",
            margin: "0 auto",
            textAlign: "center",
          }}
        >
          <h2
            style={{
              fontSize: "clamp(2.5rem, 5vw, 4rem)",
              fontWeight: "900",
              lineHeight: "1.2",
              marginBottom: "4rem",
              color: "#fff",
            }}
          >
            Bing Ads Campaign Types{" "}
            <span
              style={{
                background: "linear-gradient(135deg, #0078D4, #FFB900)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
              }}
            >
              We Optimize
            </span>
          </h2>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "2rem",
            }}
          >
            {[
              {
                icon: Search,
                title: "Search Campaigns",
                description: "Target high-intent keywords with premium placement in Bing search results, reaching professional audiences ready to convert.",
                color: "#0078D4",
              },
              {
                icon: Target,
                title: "Shopping Campaigns",
                description: "Showcase your products with rich merchant listings that stand out in Bing's less crowded shopping results.",
                color: "#FFB900",
              },
              {
                icon: Users,
                title: "Audience Campaigns",
                description: "Leverage Microsoft's unique audience data to target professionals, decision-makers, and high-value demographics.",
                color: "#53B039",
              },
              {
                icon: DollarSign,
                title: "Dynamic Search Ads",
                description: "Automatically generate ads based on your website content, capturing long-tail searches at lower costs.",
                color: "#0078D4",
              },
            ].map((service, index) => {
              const IconComponent = service.icon;
              return (
                <div
                  key={index}
                  ref={addCardRef}
                  style={{
                    position: "relative",
                    background: "rgba(255, 255, 255, 0.02)",
                    backdropFilter: "blur(20px)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: "24px",
                    padding: "2.5rem",
                    cursor: "pointer",
                    overflow: "hidden",
                    transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-10px) scale(1.02)";
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)";
                    e.currentTarget.style.borderColor = `${service.color}40`;
                    e.currentTarget.style.boxShadow = `0 20px 40px ${service.color}20`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0) scale(1)";
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.02)";
                    e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.1)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "1px",
                      background: `linear-gradient(90deg, transparent, ${service.color}80, transparent)`,
                    }}
                  />

                  <div
                    style={{
                      width: "70px",
                      height: "70px",
                      background: `linear-gradient(135deg, ${service.color}, ${service.color}80)`,
                      borderRadius: "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: "2rem",
                      color: "#000",
                      boxShadow: `0 8px 25px ${service.color}40`,
                    }}
                  >
                    <IconComponent size={32} />
                  </div>

                  <h3
                    style={{
                      fontSize: "1.4rem",
                      fontWeight: "700",
                      marginBottom: "1rem",
                      color: "#fff",
                      lineHeight: "1.3",
                    }}
                  >
                    {service.title}
                  </h3>
                  <p
                    style={{
                      color: "rgba(255, 255, 255, 0.7)",
                      lineHeight: "1.7",
                      fontSize: "1rem",
                    }}
                  >
                    {service.description}
                  </p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Performance Tuning */}
        <section
          ref={addSectionRef}
          style={{
            position: "relative",
            zIndex: 10,
            padding: "6rem 2rem",
            maxWidth: "1200px",
            margin: "0 auto",
            textAlign: "center",
          }}
        >
          <h2
            style={{
              fontSize: "clamp(2.5rem, 5vw, 4rem)",
              fontWeight: "900",
              lineHeight: "1.2",
              marginBottom: "2rem",
              color: "#fff",
            }}
          >
            <span
              style={{
                background: "linear-gradient(135deg, #0078D4, #FFB900)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
              }}
            >
              Strategic
            </span>{" "}
            Bing Optimization
          </h2>

          <p
            style={{
              fontSize: "1.2rem",
              color: "rgba(255, 255, 255, 0.8)",
              marginBottom: "4rem",
            }}
          >
            Our Bing-specific approach maximizes the platform's unique advantages:
          </p>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))",
              gap: "2rem",
              marginBottom: "3rem",
            }}
          >
            {[
              {
                icon: Target,
                title: "Bing-specific keyword research and gap analysis",
                description: "Identify high-value keywords with lower competition unique to Bing's search patterns",
                color: "#0078D4",
              },
              {
                icon: Users,
                title: "Professional audience targeting optimization",
                description: "Leverage Bing's business-focused user base for higher-value conversions",
                color: "#FFB900",
              },
              {
                icon: DollarSign,
                title: "Cost-efficiency maximization strategies",
                description: "Take advantage of lower CPCs while maintaining premium ad positions",
                color: "#53B039",
              },
            ].map((item, index) => {
              const IconComponent = item.icon;
              return (
                <div
                  key={index}
                  ref={addCardRef}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "1.5rem",
                    padding: "2rem",
                    background: "rgba(255, 255, 255, 0.02)",
                    backdropFilter: "blur(20px)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: "20px",
                    textAlign: "left",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)";
                    e.currentTarget.style.transform = "translateX(10px)";
                    e.currentTarget.style.borderColor = `${item.color}40`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.02)";
                    e.currentTarget.style.transform = "translateX(0)";
                    e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.1)";
                  }}
                >
                  <div
                    style={{
                      width: "60px",
                      height: "60px",
                      background: `linear-gradient(135deg, ${item.color}, ${item.color}80)`,
                      borderRadius: "16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#000",
                      flexShrink: 0,
                    }}
                  >
                    <IconComponent size={28} />
                  </div>
                  <div>
                    <h4
                      style={{
                        fontSize: "1.2rem",
                        fontWeight: "700",
                        marginBottom: "0.5rem",
                        color: "#fff",
                      }}
                    >
                      {item.title}
                    </h4>
                    <p
                      style={{
                        color: "rgba(255, 255, 255, 0.7)",
                        lineHeight: "1.6",
                      }}
                    >
                      {item.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <div
            style={{
              background: "rgba(255, 255, 255, 0.02)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              borderRadius: "20px",
              padding: "2rem",
              textAlign: "center",
            }}
          >
            <p
              style={{
                fontSize: "1.2rem",
                fontWeight: "600",
                color: "#fff",
                background: "linear-gradient(135deg, #0078D4, #FFB900)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
              }}
            >
              Get better results for less investment with Bing's untapped potential.
            </p>
          </div>
        </section>

        {/* Final CTA */}
        <section
          ref={addSectionRef}
          style={{
            position: "relative",
            zIndex: 10,
            padding: "6rem 2rem",
            maxWidth: "1200px",
            margin: "0 auto",
            textAlign: "center",
          }}
        >
          <div
            style={{
              background: "rgba(255, 255, 255, 0.02)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              borderRadius: "32px",
              padding: "4rem",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "2px",
                background: "linear-gradient(90deg, transparent, rgba(0, 120, 212, 0.8), rgba(255, 185, 0, 0.8), transparent)",
              }}
            />

            <h2
              style={{
                fontSize: "clamp(2.5rem, 5vw, 4rem)",
                fontWeight: "900",
                lineHeight: "1.2",
                marginBottom: "2rem",
                color: "#fff",
              }}
            >
              Ready to Dominate{" "}
              <span
                style={{
                  background: "linear-gradient(135deg, #0078D4, #FFB900, #53B039)",
                  backgroundSize: "200% 100%",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  color: "transparent",
                  animation: "gradientShift 3s ease-in-out infinite",
                }}
              >
                Bing's Blue Ocean?
              </span>
            </h2>

            <p
              style={{
                fontSize: "clamp(1rem, 1.2vw, 1.2rem)",
                color: "rgba(255, 255, 255, 0.8)",
                lineHeight: "1.7",
                maxWidth: "700px",
                margin: "0 auto 3rem",
              }}
            >
              Book a free consultation with our Bing Ads specialists. We'll show you how to tap into this 
              underutilized traffic source and achieve better ROI than your Google campaigns.
            </p>
                  <Link to="/consultation">
            <button
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "1rem",
                padding: "1.5rem 3rem",
                background: "linear-gradient(135deg, #0078D4, #FFB900, #53B039)",
                backgroundSize: "200% 100%",
                color: "#000",
                border: "none",
                borderRadius: "50px",
                fontSize: "1.2rem",
                fontWeight: "700",
                cursor: "pointer",
                transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                boxShadow: "0 10px 30px rgba(0, 120, 212, 0.3)",
                animation: "gradientShift 3s ease-in-out infinite",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-5px) scale(1.05)";
                e.currentTarget.style.boxShadow = "0 20px 50px rgba(0, 120, 212, 0.5)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0) scale(1)";
                e.currentTarget.style.boxShadow = "0 10px 30px rgba(0, 120, 212, 0.3)";
              }}
            >
              <Play size={24} />
              Unlock Bing's Hidden Potential
              <ArrowRight size={24} />
            </button>
            </Link> 
          </div>
        </section>
      </div>

      <style jsx>{`
        @keyframes particleFloat {
          0% { transform: translateY(0px); }
          100% { transform: translateY(-100px); }
        }
        
        @keyframes gradientShift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        
        @keyframes shimmer {
          0% { left: -50%; }
          100% { left: 120%; }
        }

        @media (max-width: 768px) {
          section {
            padding: 3rem 1rem !important;
          }
        }
      `}</style>
    </>
  );
};

export default BingAdsPage;